import React, { useState, useEffect } from 'react';
import { ConsultorMetrics, FunnelData, TrendData } from '../types';
import { PRICING } from '../constants';
import { leadsService } from '../services/api';

const ConsultorDashboard: React.FC = () => {
  const [consultorMetrics, setConsultorMetrics] = useState<ConsultorMetrics[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConsultorMetrics = async () => {
      try {
        setLoading(true);
        const response = await leadsService.getConsultorMetrics({ 
          period: selectedPeriod 
        });
        
        // Processar os dados recebidos para garantir que tenham o formato esperado
        const processedMetrics = response.map((consultor: any) => ({
          ...consultor,
          valor_total_vendas: consultor.leads_convertidas * PRICING.PRODUTO_PRECO,
          taxa_conversao: consultor.total_leads > 0 
            ? (consultor.leads_convertidas / consultor.total_leads) * 100 
            : 0
        }));
        
        setConsultorMetrics(processedMetrics);
      } catch (error) {
        console.error('Erro ao carregar métricas dos consultores:', error);
        
        // Fallback para dados mock em caso de erro
        const fallbackMetrics: ConsultorMetrics[] = [
          {
            consultor_id: '1',
            consultor_name: 'João Silva',
            total_leads: 45,
            leads_convertidas: 12,
            taxa_conversao: 26.7,
            valor_total_vendas: 12 * PRICING.PRODUTO_PRECO,
            leads_por_status: {
              novo: 8,
              contato_inicial: 10,
              em_negociacao: 7,
              proposta_enviada: 5,
              fechado_ganho: 12,
              fechado_perdido: 3
            },
            tempo_medio_conversao: 15.5,
            ultima_atividade: '2024-01-15T10:30:00Z'
          }
        ];
        setConsultorMetrics(fallbackMetrics);
      } finally {
        setLoading(false);
      }
    };

    loadConsultorMetrics();
  }, [selectedPeriod]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPerformanceColor = (taxa: number) => {
    if (taxa >= 30) return 'text-green-600 bg-green-100';
    if (taxa >= 20) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'bg-blue-500',
      contato_inicial: 'bg-yellow-500',
      em_negociacao: 'bg-orange-500',
      proposta_enviada: 'bg-purple-500',
      fechado_ganho: 'bg-green-500',
      fechado_perdido: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const totalLeads = consultorMetrics.reduce((sum, c) => sum + c.total_leads, 0);
  const totalConvertidas = consultorMetrics.reduce((sum, c) => sum + c.leads_convertidas, 0);
  const totalVendas = consultorMetrics.reduce((sum, c) => sum + c.valor_total_vendas, 0);
  const taxaGeralConversao = totalLeads > 0 ? (totalConvertidas / totalLeads) * 100 : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600">Carregando métricas dos dados reais...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">📊 Performance por Consultor</h2>
        <div className="flex space-x-2">
          {[
            { key: '7d', label: '7 dias' },
            { key: '30d', label: '30 dias' },
            { key: '90d', label: '90 dias' },
            { key: '1y', label: '1 ano' }
          ].map(period => (
            <button
              key={period.key}
              onClick={() => setSelectedPeriod(period.key as any)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === period.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm">📈</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total de Leads
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {totalLeads}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm">✅</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Convertidas
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {totalConvertidas}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-sm">🎯</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Taxa de Conversão
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {taxaGeralConversao.toFixed(1)}%
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 text-sm">💰</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Vendas Totais
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {formatCurrency(totalVendas)}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Performance */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Performance Individual</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consultor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo Médio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pipeline
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {consultorMetrics
                .sort((a, b) => b.taxa_conversao - a.taxa_conversao)
                .map((consultor) => (
                <tr key={consultor.consultor_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-indigo-600 text-sm font-medium">
                            {consultor.consultor_name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {consultor.consultor_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {consultor.consultor_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {consultor.total_leads} total
                    </div>
                    <div className="text-sm text-green-600">
                      {consultor.leads_convertidas} convertidas
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerformanceColor(consultor.taxa_conversao)}`}>
                      {consultor.taxa_conversao.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(consultor.valor_total_vendas)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {Number(consultor.tempo_medio_conversao || 0).toFixed(1)} dias
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      {Object.entries(consultor.leads_por_status).map(([status, count]) => (
                        count > 0 && (
                          <div
                            key={status}
                            className="flex items-center text-xs"
                            title={`${status}: ${count} leads`}
                          >
                            <div 
                              className={`w-2 h-2 rounded-full ${getStatusColor(status)} mr-1`}
                            ></div>
                            <span>{count}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🏆 Top Conversão</h3>
          <div className="space-y-3">
            {consultorMetrics
              .sort((a, b) => b.taxa_conversao - a.taxa_conversao)
              .slice(0, 3)
              .map((consultor, index) => (
              <div key={consultor.consultor_id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-100 text-gray-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    <span className="text-xs font-medium">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {consultor.consultor_name}
                  </span>
                </div>
                <span className="text-sm font-semibold text-green-600">
                  {consultor.taxa_conversao.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">💰 Top Vendas</h3>
          <div className="space-y-3">
            {consultorMetrics
              .sort((a, b) => b.valor_total_vendas - a.valor_total_vendas)
              .slice(0, 3)
              .map((consultor, index) => (
              <div key={consultor.consultor_id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-100 text-gray-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    <span className="text-xs font-medium">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {consultor.consultor_name}
                  </span>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  {formatCurrency(consultor.valor_total_vendas)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultorDashboard;